import { BaseService } from './base.service';
import {
  MemberProfile,
  UpdateMemberProfileDto,
} from '@/types/member.types';

export interface BoxPro {
  id: string;
  firstName: string;
  lastName: string;
  profilePhoto?: string;
  bio?: string;
  experience?: string;
  certifications?: string;
  facebookUrl?: string;
  instagramUrl?: string;
}

export interface BoxProHoursInterval {
  /** HH:mm, location time */
  from: string;
  to: string;
}

/** A Box Pro's general availability (GHL "Work Hours"). */
export interface BoxProAvailability {
  hasSchedule: boolean;
  timezone: string;
  /** Sunday first; empty intervals mean a day off. */
  week: { day: string; intervals: BoxProHoursInterval[] }[];
  /** Date-specific hours (yyyy-MM-dd) in the next 14 days. */
  dateOverrides: { date: string; intervals: BoxProHoursInterval[] }[];
}

class MemberService extends BaseService {
  constructor() {
    super('/member-profile');
  }

  async getMemberProfile(): Promise<MemberProfile> {
    return this.get<MemberProfile>('');
  }

  async updateMemberProfile(data: UpdateMemberProfileDto): Promise<MemberProfile> {
    return this.put<MemberProfile>('', data);
  }

  /**
   * Flip a past_due member back to active after their new card is saved.
   * The API writes the status to the CRM first, so a resolved promise means
   * both systems agree the membership is active again.
   */
  async reactivateAfterPaymentUpdate(): Promise<MemberProfile> {
    return this.post<MemberProfile>('/reactivate');
  }

  /**
   * A member in good standing changed their card.
   *
   * Separate from `/reactivate` on purpose: that endpoint restores a past-due
   * membership, which is meaningless for someone who was never blocked. Both
   * tell ops to move the new card onto the Smartlev subscription; only this one
   * leaves membership status alone.
   */
  async notifyPaymentMethodUpdated(): Promise<MemberProfile> {
    return this.post<MemberProfile>('/payment-method-updated');
  }

  async deleteOwnAccount(): Promise<{ message: string }> {
    return this.delete<{ message: string }>('');
  }

  /**
   * Replace the member's avatar.
   *
   * Returns the updated member, NOT `{ avatarUrl }` as the old
   * signature claimed — and its `avatar` is the S3 key rather than a
   * URL, because only `GET /member-profile` signs it. Callers should
   * refetch the profile rather than render anything off this.
   */
  async uploadAvatar(file: File): Promise<MemberProfile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.post<MemberProfile>('/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async removeAvatar(): Promise<MemberProfile> {
    return this.delete<MemberProfile>('/avatar');
  }

  async getAvatarUrl(): Promise<{ url: string }> {
    return this.get<{ url: string }>('/avatar/url');
  }

  async getBoxPros(): Promise<BoxPro[]> {
    return this.get<BoxPro[]>('/box-pros');
  }

  async getBoxProAvailability(userId: string): Promise<BoxProAvailability> {
    return this.get<BoxProAvailability>(`/box-pros/${userId}/availability`);
  }
}

export default new MemberService();
